import * as React from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Chat } from "@/components/ui/chat/chat";
import { cn } from "@/lib/utils";
import {
  usePersonation,
  DEFAULT_LAUNCHER_AVATAR_SIZE,
} from "@/lib/personation-context";
import { withAlpha } from "@/lib/color";

export type ChatWidgetProps = {
  roomId?: string;
  isLeft?: boolean;
};

export function ChatWidget(props: ChatWidgetProps) {
  const {
    botAvatarUrl,
    botName,
    launcherCallToAction,
    launcherAvatarSize,
    companyMainColor,
  } = usePersonation();
  const [isVisible, setIsVisible] = React.useState(false);
  const [isFullScreen, setIsFullScreen] = React.useState(false);

  const toggleVisibility = React.useCallback((next: boolean) => {
    setIsVisible(next);
    if (!next) {
      setIsFullScreen(false);
    }
  }, []);

  const handleFullScreenToggle = React.useCallback((next: boolean) => {
    setIsFullScreen(next);
    if (!next) {
      setIsVisible(true);
    }
  }, []);

  const isBubbleBeforeAvatar = !props.isLeft;
  const sidePosition = props.isLeft ? "left-6" : "right-6";
  const alignment = props.isLeft ? "items-start" : "items-end";
  const showLauncherCta =
    Boolean(launcherCallToAction) && !isVisible && !isFullScreen;

  const resolvedAvatarSize = React.useMemo(() => {
    const size =
      launcherAvatarSize && launcherAvatarSize > 0
        ? launcherAvatarSize
        : DEFAULT_LAUNCHER_AVATAR_SIZE;
    return Math.max(56, size);
  }, [launcherAvatarSize]);

  const buttonDiameter = React.useMemo(
    () => resolvedAvatarSize + 16,
    [resolvedAvatarSize]
  );
  const bubbleOverlap = React.useMemo(
    () => Math.max(16, resolvedAvatarSize * 0.4),
    [resolvedAvatarSize]
  );

  const bubbleStyles = React.useMemo(() => {
    const accent = companyMainColor?.trim() || "#0f172a";
    return {
      backgroundColor: "#fff",
      borderColor: withAlpha(accent, 0.35),
      color: accent,
    } satisfies React.CSSProperties;
  }, [companyMainColor]);

  return (
    <>
      <div
        className={cn(
          "pointer-events-none fixed  bottom-6 z-40 flex flex-col position-relative",
          sidePosition,
          alignment
        )}
      >
        <div
          className={cn(
            "pointer-events-auto flex items-center z-10",
            isBubbleBeforeAvatar ? "flex-row" : "flex-row-reverse"
          )}
        >
          {showLauncherCta && (
            <div
              data-testid="chat-launcher-cta"
              className="relative flex max-w-[280px] flex-col justify-center rounded-3xl px-5 py-3 text-base font-medium leading-relaxed shadow-2xl"
              style={{
                ...bubbleStyles,
                // marginRight: isBubbleBeforeAvatar ? -bubbleOverlap : 12,
                marginRight: 4,
                marginLeft: isBubbleBeforeAvatar ? 12 : -bubbleOverlap,
                boxShadow:
                  "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
              }}
            >
              <span
                className={isBubbleBeforeAvatar ? "text-left" : "text-right"}
              >
                {launcherCallToAction}
              </span>
              {/* <span
                aria-hidden="true"
                className="absolute bottom-3 h-8 w-8 rounded-md"
                style={{
                  backgroundColor: bubbleStyles.backgroundColor,
                  borderBottom: `1px solid ${bubbleStyles.borderColor}`,
                  borderLeft: isBubbleBeforeAvatar
                    ? `1px solid ${bubbleStyles.borderColor}`
                    : undefined,
                  borderRight: !isBubbleBeforeAvatar
                    ? `1px solid ${bubbleStyles.borderColor}`
                    : undefined,
                  right: isBubbleBeforeAvatar ? `-${bubbleOverlap / 2}px` : undefined,
                  left: !isBubbleBeforeAvatar ? `-${bubbleOverlap / 2}px` : undefined,
                  transform: isBubbleBeforeAvatar
                    ? "rotate(-18deg) translateY(15%)"
                    : "rotate(18deg) translateY(15%)",
                }}
              /> */}
            </div>
          )}
          <button
            type="button"
            data-testid="chat-launcher-button"
            onClick={() => toggleVisibility(true)}
            className="inline-flex items-center justify-center rounded-full border-border bg-background shadow-2xl transition-transform focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary hover:scale-[1.02]"
            style={{
              width: buttonDiameter,
              height: buttonDiameter,
              boxShadow:
                "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
            }}
          >
            <Avatar
              style={{ width: resolvedAvatarSize, height: resolvedAvatarSize }}
            >
              <AvatarImage src={botAvatarUrl} alt={botName} />
              <AvatarFallback>{botName?.[0] ?? "B"}</AvatarFallback>
            </Avatar>
          </button>
        </div>
      </div>

      <div
        role="presentation"
        data-testid="chat-overlay"
        onClick={() => {
          if (!isFullScreen) {
            toggleVisibility(false);
          }
        }}
        className={cn(
          "fixed inset-0 z-40 transition-opacity duration-200",
          isFullScreen
            ? "pointer-events-none opacity-0"
            : isVisible
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        )}
      />

      <div
        aria-hidden={!(isVisible || isFullScreen)}
        data-testid="chat-frame"
        className={cn(
          "fixed z-50 transition-all duration-200",
          `bottom-6 ${sidePosition}`,
          isVisible || isFullScreen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0 translate-y-4"
        )}
      >
        <Chat.Default
          roomId={props.roomId}
          className={cn(
            "shadow-2xl",
            isFullScreen
              ? "w-[1500px] max-w-[calc(90vw-3rem)] max-h-[80vh] min-h-[70vh]"
              : "w-[420px] max-w-[calc(100vw-3rem)]"
          )}
          isFullScreen={isFullScreen}
          onFullScreenToggle={handleFullScreenToggle}
          onCloseRequest={() => toggleVisibility(false)}
        />
      </div>
    </>
  );
}
